import axios from 'axios';
import { Drawer, DrawerContent, DrawerNavigation } from '@progress/kendo-react-layout';
import { useEffect, useState, useMemo } from 'react';
import { FaTimesCircle } from 'react-icons/fa';
import ServiceRemindersGrid from '../../components/grids/ServiceRemindersGrid';
import { getServiceReminderGrid, getServiceTypes } from '../../data/serviceReminderData';
import { ServiceReminderData } from './serviceReminders.types';
import EditServiceRemindersForm from '../../components/FormComps/ServiceReminders/EditServiceRemindersForm';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Dialog } from '@progress/kendo-react-dialogs';
import AddClientForm from '../../components/FormComps/Clients/AddClientForm';
import AddSiteForm from '../../components/FormComps/Sites/AddSiteForm';
import { getClients, submitClient } from '../../data/clientData';
import { clientsGrid, getSiteTypes, dropDown } from '../../data/interfaces';
import { useToast } from '../../contexts/ToastContext';
import { getSites, submitSite } from '../../data/siteData';
import AccountForm from '../../components/FormComps/Accounts/AccountForm';

interface ServiceRemindersProps {
  lockEdit: boolean;
}

type ServiceTypeOption = { id: number; name: string };

const ServiceReminders = (props: ServiceRemindersProps) => {
  const [expanded, setExpanded] = useState(false);
  const [serviceReminderData, setServiceReminderData] = useState<ServiceReminderData[]>([]);
  const [selectedId, setSelectedId] = useState<number>(0);
  const [formVisible, setFormVisible] = useState(false);
  const [mode, setMode] = useState<"edit" | "add">("edit");
  const [showDead, setShowDead] = useState(false);
  const [showProgressed, setShowProgressed] = useState(false);
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeOption[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<dropDown | null>(null);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [jobDetails, setJobDetails] = useState<{ client: string, site: string, workType: string } | undefined>(undefined);
  const [visibleClientModal, setVisibleClientModal] = useState<boolean>(false);
  const [visibleSiteModal, setVisibleSiteModal] = useState<boolean>(false);
  const [visibleDeleteConfirm, setVisibleDeleteConfirm] = useState(false);
  const [visibleWorktypeModal, setVisibleWorktypeModal] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientsGrid[]>([]);
  const [siteData, setSiteData] = useState<getSiteTypes[]>([]);
  const [showDeletedState, setShowDeletedState] = useState(false);
  const { showToast } = useToast();

  const dropdownData: dropDown[] = useMemo(() => [
    { value: 0, text: 'All service types' },
    ...serviceTypes.map(st => ({ value: st.id, text: st.name }))
  ], [serviceTypes]);

  const dropdownValue = useMemo(() => {
    return dropdownData.find(item => item.value === selectedServiceType?.value) || dropdownData[0];
  }, [selectedServiceType, dropdownData]);

  const toggleModal = (type: string) => {
    switch (type.toLowerCase()) {
      case 'client':
        setVisibleClientModal(!visibleClientModal);
        break;
      case 'site':
        setVisibleSiteModal(!visibleSiteModal);
        break;
      case 'worktype':
        setVisibleWorktypeModal(!visibleWorktypeModal);
        break;
      case 'deleteconfirm':
        setVisibleDeleteConfirm(!visibleWorktypeModal);
        break;
      default:
        console.log('Invalid modal type.');
        break;
    }
  };

  const newClientHandler = (dataItem: any) => {
    const clientObj = {
      clientName: dataItem.clientName,
      accountRef: dataItem.accountRef ?? null,
      contactName: dataItem.contactFirstName + ' ' + dataItem.contactLastName,
      phone: dataItem.phone,
      email: dataItem.email ?? null,
      clientAddr: dataItem.clientAddr,
      postcode: dataItem.postcode,
      phone2: dataItem.phone2 ?? null,
      fax: dataItem.fax ?? null,
      tradeContact: dataItem.tradeContact ?? null,
      email2: dataItem.email2 ?? null,
      email3: dataItem.email3 ?? null,
      websiteUrl: dataItem.websiteUrl ?? null,
      vatNum: dataItem.vatNum ?? null,
      directdebitEmail: dataItem.directdebitEmail ?? null,
    };
    submitClient(clientObj).then(() => {
      getClients(showDeletedState).then(setClientData);
    });
    toggleModal('client');
    showToast('Client succesfully added', 'success');
  };

  const newSiteHandler = (dataItem: any) => {
    const siteObj = {
      siteAddr: dataItem.siteAddr,
      postcode: dataItem.postcode,
    };
    submitSite(siteObj).then(() => {
      getSites(false).then(setSiteData);
    });
    toggleModal('site');
    showToast('Site succesfully added', 'success');
  };

  const handleExpanded = () => setExpanded(prev => !prev);
  const handleSelect = () => setExpanded(false);

  const getServiceReminderData = async (
    showCompleted: boolean,
    showDeleted: boolean,
    serviceTypeId: number = 0
  ) => {
    const data = await getServiceReminderGrid(showCompleted, showDeleted, serviceTypeId);
    setServiceReminderData(data);
  };

  const handleEditForm = (id: number) => {
    setSelectedId(id);
    setFormVisible(true);
    setMode("edit");
    handleExpanded();
  };

  const handleAddForm = () => {
    setFormVisible(true);
    setMode('add');
    handleExpanded();
  };

  useEffect(() => {
    getServiceReminderData(false, false, dropdownValue.value);
  }, []);

  useEffect(() => {
    getServiceReminderData(showDead, showProgressed, dropdownValue.value);
  }, [showDead, showProgressed, dropdownValue]);
  

  useEffect(() => {
    (async () => {
      const result = await getServiceTypes();
      setServiceTypes(result);
    })();
  }, []);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) {
      setSelectedId(Number(id));
      setFormVisible(true);
      setMode("edit");
      setExpanded(true);
    }
  }, []);

  useEffect(() => {
    if (location.pathname === "/service-reminders/add") {
      const client = searchParams.get("client") || "";
      const site = searchParams.get("site") || "";
      const workType = searchParams.get("workType") || "";
      setJobDetails({ client, site, workType });
      setMode("add");
      setExpanded(true);
      setFormVisible(true);
    }
  }, []);

  const [addAccountVisible, setAddAccountVisible] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [accountMode, setAccountMode] = useState<"add" | "edit">("add");

  useEffect(() => {
    setAccountMode(mode);
  }, [mode]);

  return (
    <div>
      <Drawer expanded={expanded} position="end" width={750} mode="overlay" animation={{ duration: 400 }} onOverlayClick={handleExpanded} onSelect={handleSelect}>
        <DrawerContent>
          <DrawerNavigation>
            {formVisible && (
              <>
                <div className="form__title__container">
                  <h1 className="form__title">{mode === "edit" ? "Edit Service Reminder" : "Add Service Reminder"}</h1>
                  <FaTimesCircle className="closeform" onClick={handleExpanded} />
                </div>
                <EditServiceRemindersForm
                  lockEdit={props.lockEdit}
                  id={selectedId}
                  mode={mode}
                  fromJob={jobDetails}
                  getServiceReminderData={getServiceReminderData}
                  handleExpanded={handleExpanded}
                  onAddClientToggle={() => toggleModal('client')}
                  onAddSiteToggle={() => toggleModal('site')}
                  setAddAccountVisible={setAddAccountVisible}
                  setAccountMode={setAccountMode}
                  setSelectedAccountId={setSelectedAccountId}
                />
              </>
            )}
          </DrawerNavigation>

          <ServiceRemindersGrid
            lockEdit={props.lockEdit}
            onAddToggle={handleAddForm}
            onEditToggle={handleEditForm}
            onData={serviceReminderData}
            onShowDead={() => setShowDead(!showDead)}
            onShowProgressed={() => setShowProgressed(!showProgressed)}
            dropdownData={dropdownData}
            dropdownValue={dropdownValue}
            selectedServiceType={selectedServiceType}
            setSelectedServiceType={setSelectedServiceType}
          />
        </DrawerContent>
      </Drawer>

      {visibleClientModal && (
        <Dialog width={500} height={700} title={'Please confirm'} onClose={() => toggleModal('client')}>
          <AddClientForm onClientSubmit={newClientHandler} />
        </Dialog>
      )}

      {visibleSiteModal && (
        <Dialog width={500} height={700} title={'Please confirm'} onClose={() => toggleModal('site')}>
          <AddSiteForm onSiteSubmit={newSiteHandler} />
        </Dialog>
      )}

      {addAccountVisible && (
        <Dialog width={"70vw"} height={"90vh"} title={"Add new account"} onClose={() => setAddAccountVisible(false)}>
          <AccountForm
            onClose={() => setAddAccountVisible(false)}
            mode={accountMode}
            id={selectedAccountId || null}
            setAccountId={(id) => setSelectedAccountId(id)}
          />
        </Dialog>
      )}
    </div>
  );
};

export default ServiceReminders;
